import { type SiteMasterCredentialsListDto, useSiteMasterCredentialsListGetAll } from "@crowdcoursing/api/openapi";
import { useMemo } from "react";

/** Catalog changes rarely; avoid refetching on every Candidate Search visit. */
export const SITE_MASTER_CREDENTIALS_STALE_TIME_MS = 24 * 60 * 60 * 1000;

/** Keep cached rows available across route changes within the app session. */
export const SITE_MASTER_CREDENTIALS_GC_TIME_MS = 24 * 60 * 60 * 1000;

export interface SiteMasterCredentialSearchOption {
  code: string;
  title: string;
  majorSocTitle: string;
}

export function mapSiteMasterCredentialsToSearchOptions(
  rows: SiteMasterCredentialsListDto[] | undefined,
): SiteMasterCredentialSearchOption[] {
  if (!rows?.length) {
    return [];
  }

  return rows.flatMap((row) => {
    const title = row.certificationTitle?.trim();
    if (!title) {
      return [];
    }
    return [
      {
        code: row.certificationCode?.trim() ?? "",
        majorSocTitle: row.majorSocTitle?.trim() ?? "",
        title,
      },
    ];
  });
}

/**
 * Loads the environment master credentials catalog with a long-lived React Query cache.
 * Call from Candidate Search (or future Organization → Candidates tab); not from app shell.
 */
export function useSiteMasterCredentialsCatalog(enabled = true) {
  const query = useSiteMasterCredentialsListGetAll(
    {},
    {
      enabled,
      gcTime: SITE_MASTER_CREDENTIALS_GC_TIME_MS,
      refetchOnMount: false,
      staleTime: SITE_MASTER_CREDENTIALS_STALE_TIME_MS,
    },
  );

  const searchOptions = useMemo(() => mapSiteMasterCredentialsToSearchOptions(query.data), [query.data]);

  return {
    ...query,
    searchOptions,
  };
}
